import * as React from "react";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface SplitLayoutProps {
  children: React.ReactNode;
  panel: React.ReactNode;
  isPanelOpen: boolean;
  onPanelClose: () => void;
  panelTitle?: string;
  panelWidth?: string;
  className?: string;
}

/**
 * SplitLayout - Layout component that displays main content with an optional side panel
 * 
 * When panel is open, the main content compresses to make room for the panel.
 * No overlay/darkening effect - both are visible simultaneously.
 * 
 * Usage:
 * <SplitLayout
 *   isPanelOpen={isPanelOpen}
 *   onPanelClose={() => setIsPanelOpen(false)}
 *   panelTitle="Details"
 *   panel={<DetailContent />}
 * >
 *   <DataTable ... />
 * </SplitLayout>
 */
export function SplitLayout({
  children,
  panel,
  isPanelOpen,
  onPanelClose,
  panelTitle,
  panelWidth = "400px",
  className,
}: SplitLayoutProps) {
  return (
    <div className={cn("flex gap-4 flex-1 overflow-hidden -m-10", className)}>
      {/* Main Content Area - compresses when panel is open */}
      <div
        className={cn(
          "flex-1 overflow-auto transition-all duration-300 ease-in-out p-10"
        )}
      >
        {children}
      </div>

      {/* Side Panel - slides in from right, extends to edges */}
      <div
        className={cn(
          "bg-card border-l border-gray-200 flex flex-col transition-all duration-300 ease-in-out",
          isPanelOpen ? "opacity-100" : "opacity-0 w-0"
        )}
        style={{
          width: isPanelOpen ? panelWidth : "0px",
          minWidth: isPanelOpen ? panelWidth : "0px",
        }}
      >
        {isPanelOpen && (
          <>
            {/* Panel Header - extends full width to right edge */}
            {panelTitle && (
              <div className="flex items-center justify-between pl-6 pr-6 py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg font-semibold text-foreground">
                  {panelTitle}
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onPanelClose}
                  className="h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {panel}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

